// Подсчёты формы точки: сколько уходит из машины, сколько забрано у точки,
// пределы для счётчиков и тело запроса закрытия.

import type { PointDraft } from '@/lib/drafts';
import type { PointScreen, VisitCloseBody, VisitSwap } from '@/services/courier';

export const r3 = (n: number) => Math.round(n * 1000) / 1000;

export interface Usage {
  /** Уходит из машины: отдал + выдал взамен. */
  out: Map<string, number>;
  /** Забрано у точки (возвраты и обмены). */
  taken: Map<string, number>;
}

function add(m: Map<string, number>, id: string, q: number) {
  if (q > 0) m.set(id, r3((m.get(id) ?? 0) + q));
}

export function usageOf(draft: PointDraft): Usage {
  const out = new Map<string, number>();
  const taken = new Map<string, number>();
  for (const [id, l] of Object.entries(draft.lines)) {
    add(out, id, l.d);
    add(out, id, l.r);
    add(taken, id, l.r);
  }
  for (const s of draft.swaps) {
    add(out, s.toItemId, s.toQty);
    add(taken, s.fromItemId, s.qty);
  }
  return { out, taken };
}

/** Сколько можно забрать у точки по каждому товару; null — не знаем (нет связи), проверит сервер. */
export function returnableMap(screen: PointScreen, offline: boolean): Map<string, number> | null {
  if (offline) return null;
  return new Map(screen.returnables.map((r) => [r.id, r.returnable]));
}

export function bodyOf(draft: PointDraft, menuIds: Set<string>): Pick<VisitCloseBody, 'lines' | 'swaps'> {
  const lines: VisitCloseBody['lines'] = [];
  const swaps: VisitSwap[] = [];
  for (const [itemId, l] of Object.entries(draft.lines)) {
    if (!menuIds.has(itemId)) continue;
    if (l.d > 0) lines.push({ itemId, delivered: l.d });
    if (l.r > 0) swaps.push({ fromItemId: itemId, qty: l.r, toItemId: itemId, toQty: l.r });
  }
  for (const s of draft.swaps) if (s.qty > 0 && s.toQty > 0) swaps.push(s);
  return { lines, swaps };
}

type VisitLine = NonNullable<PointScreen['visit']>['lines'][number];

/** Делит количество на k частей (до тысячных), остаток — в последнюю. */
function shares(total: number, k: number): number[] {
  const base = r3(total / k);
  const out = Array.from({ length: k }, () => base);
  out[k - 1] = r3(total - base * (k - 1));
  return out;
}

/**
 * Пары «забрал → отдал» для разных товаров. Сервер хранит только итоги по
 * товарам, поэтому пары собираем заново так, чтобы итоги по каждому товару
 * совпали: на склад, накладную и себестоимость обмена влияют только они.
 */
export function pairSwaps(froms: Array<[string, number]>, tos: Array<[string, number]>): VisitSwap[] {
  if (!froms.length || !tos.length) return [];
  const swaps: VisitSwap[] = [];
  const k = Math.min(froms.length, tos.length);
  for (let i = 0; i < k - 1; i++) swaps.push({ fromItemId: froms[i]![0], qty: froms[i]![1], toItemId: tos[i]![0], toQty: tos[i]![1] });
  const restFrom = froms.slice(k - 1);
  const restTo = tos.slice(k - 1);
  if (restFrom.length === 1) {
    const [fromItemId, fromQty] = restFrom[0]!;
    const parts = shares(fromQty, restTo.length);
    restTo.forEach(([toItemId, toQty], j) => swaps.push({ fromItemId, qty: parts[j]!, toItemId, toQty }));
  } else {
    const [toItemId, toQty] = restTo[0]!;
    const parts = shares(toQty, restFrom.length);
    restFrom.forEach(([fromItemId, qty], j) => swaps.push({ fromItemId, qty, toItemId, toQty: parts[j]! }));
  }
  return swaps.filter((s) => s.qty > 0 && s.toQty > 0);
}

/** Точку открыли снова: отгрузка, замены тем же товаром и обмены прошлого закрытия возвращаются в форму. */
export function draftFromVisitLines(lines: VisitLine[]): Pick<PointDraft, 'lines' | 'swaps'> {
  const draftLines: PointDraft['lines'] = {};
  const froms: Array<[string, number]> = [];
  const tos: Array<[string, number]> = [];
  for (const l of lines) {
    if (!l.itemId) continue;
    const same = r3(Math.min(l.returnedQty, l.replacedQty));
    if (l.deliveredQty > 0 || same > 0) draftLines[l.itemId] = { d: l.deliveredQty, r: same };
    if (l.returnedQty - same > 1e-9) froms.push([l.itemId, r3(l.returnedQty - same)]);
    if (l.replacedQty - same > 1e-9) tos.push([l.itemId, r3(l.replacedQty - same)]);
  }
  return { lines: draftLines, swaps: pairSwaps(froms, tos) };
}

export interface VisitMoney {
  /** Отгружено на продажу — это и есть сумма к оплате за визит. */
  delivered: number;
  /** Забрано у точки (возврат на замену и обмен) — по цене, без денег. */
  taken: number;
  /** Выдано взамен — по цене, без денег. */
  given: number;
}

/** Итоги визита в копейках по ценам из карточки точки. */
export function visitMoney(draft: PointDraft, priceOf: (id: string) => number): VisitMoney {
  let delivered = 0;
  let taken = 0;
  let given = 0;
  for (const [id, l] of Object.entries(draft.lines)) {
    delivered += Math.round(priceOf(id) * l.d);
    taken += Math.round(priceOf(id) * l.r);
    given += Math.round(priceOf(id) * l.r);
  }
  for (const s of draft.swaps) {
    taken += Math.round(priceOf(s.fromItemId) * s.qty);
    given += Math.round(priceOf(s.toItemId) * s.toQty);
  }
  return { delivered, taken, given };
}
